import UploadInvoice from '@components/upload-invoice/upload-invoice.component'
import { Button, InfluencerTransaction, LogService, Modal, NumbersHelper, ToastHelper, ZeCariocaService } from '@hub/shared'
import CardNfData from '@src/components/card-nf-data/card-nf-data.component'
import CardXmlData from '@src/components/card-xml-data/card-xml-data.component'
import { useCallback, useLayoutEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import './modal-send-nf.component.scoped.scss'

interface Props {
  open: boolean
  transactionObject: InfluencerTransaction | null
  afterSubmit?: any
  closeModal?: () => any
}

interface NfData {
  emissionDate: string
  serialNumber: string
  file: File
}

interface XMLData {
  file: any
}

export default ({ open, transactionObject, afterSubmit, closeModal }: Props) => {
  const numbersHelper = useMemo(() => new NumbersHelper(), [])
  const toast = useMemo(() => new ToastHelper().toast, [])
  const zeCariocaService = useMemo(() => new ZeCariocaService(), [])
  const logService = useMemo(() => new LogService(), [])
  logService.init('ModalSendNf Component')

  const [width, setWidth] = useState(window?.innerWidth)
  const [height, setHeight] = useState(window?.innerHeight)
  const [isLoading, setIsLoading] = useState(false)
  const [isReplaceInvoice, setIsReplaceInvoice] = useState(false)
  const [fileNf, setFileNf] = useState<NfData | null>(null)
  const [dateFile, setDateFile] = useState(null)
  const [isFileNfValid, setIsFileNfValid] = useState(false)
  const [isDateFileValid, setIsDateFileValid] = useState(false)

  const { t, i18n } = useTranslation('modalSendNf')

  const navigate = useNavigate()

  const onCloseModal = useCallback(() => {
    setIsLoading(false)
    setFileNf(null)
    setDateFile(null)
    setIsFileNfValid(false)
    setIsReplaceInvoice(false)
    setIsDateFileValid(false)
    if (closeModal && typeof closeModal === 'function') {
      closeModal()
    }
  }, [closeModal])

  const formatDate = (date: any) => {
    return date?.split('-').reverse().join('')
  }

  const sendNF = async (id?: string, file?: File, datafile?: null, serialNumber?: any, emissionDate?: any, value?: string | undefined) => {
    try {
      setIsLoading(true)
      return await zeCariocaService.postNFByTransactionId(id, (file as any)[0], serialNumber, formatDate(emissionDate), value, datafile)
    } catch (err: any) {
      logService.error({ message: 'Failed to send Invoice', error: err })
      toast.error(t('failedToSendNF'), {
        closeButton: true,
        className: 'full',
        position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
        duration: 3600000,
        notOverClick: true,
        immediately: true,
      })
      throw err?.response
    } finally {
      setIsLoading(false)
    }
  }

  const saveInvoice = async () => {
    setIsLoading(true)

    const { transactionId } = transactionObject?.transaction || {}
    const response = await sendNF(
      transactionId,
      fileNf?.file,
      dateFile,
      fileNf?.serialNumber,
      fileNf?.emissionDate,
      transactionObject?.transaction?.grossValue.toFixed(2)
    )
    if (!response?.data?.error) {
      onCloseModal()
      toast.success(t('successToSendNF'), {
        closeButton: true,
        className: 'full text-bold',
        position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
        notOverClick: true,
        immediately: true,
      })
      if (afterSubmit && typeof afterSubmit === 'function') {
        afterSubmit()
      } else {
        navigate('/payments/info')
      }
    } else {
      onCloseModal()
    }
  }

  const month = useMemo(
    () =>
      new Date(transactionObject?.transaction?.dueDate).toLocaleString(i18n.language, {
        month: 'long',
      }),
    [transactionObject, i18n.language]
  )

  const year = useMemo(() => new Date(transactionObject?.transaction?.dueDate).getFullYear(), [transactionObject])

  const isDisabled = useMemo(() => {
    return !isFileNfValid || (dateFile && !isDateFileValid) || false
  }, [isFileNfValid, dateFile, isDateFileValid])

  const nf = useMemo(
    () => transactionObject?.nfs?.find((nf) => nf?.transactionId === transactionObject?.transaction?.transactionId),
    [transactionObject]
  )

  useLayoutEffect(() => {
    const updateWidth = () => {
      setWidth(window?.innerWidth)
    }
    const updateHeight = () => {
      setHeight(window?.innerHeight)
    }
    window.addEventListener('resize', updateWidth)
    window.addEventListener('resize', updateHeight)
    return () => {
      window.removeEventListener('resize', updateWidth)
      window.removeEventListener('resize', updateHeight)
    }
  }, [])

  return (
    <Modal
      className="modal-send-nf"
      open={open}
      modalSize="md"
      onCloseChange={() => onCloseModal()}
      header={<h5 className="m-0">{t('sendingTaxDocuments')}</h5>}
      footer={
        <div className="display-flex justify-content-space-between" style={{ width: '100%' }}>
          <Button
            id="cancel-button-modal-send-nf"
            className="cancel-button-modal-send-nf"
            size="sm"
            onClick={onCloseModal}
            color="transparent"
            textColor="var(--gray)"
            borderColor="var(--gray)"
            disabled={isLoading}
          >
            <span>{t('cancel')}</span>
          </Button>
          <Button
            id="confirm-button-modal-send-nf"
            className="confirm-button-modal-send-nf"
            size="sm"
            onClick={saveInvoice}
            disabled={isDisabled}
            loading={isLoading}
          >
            <span>{t('save')}</span>
          </Button>
        </div>
      }
      forceMobileNoMargin
    >
      <div
        className="modal-send-nf-content scrollbar"
        style={{ height: width <= 991 || height < 771 ? `calc(${height}px - 12rem)` : 'auto' }}
      >
        <section className="summary display-flex-column">
          <h5 className="m-0">{t('summary')}</h5>
          <div className="box-summary p-3">
            <p className="summary-title">{`${month} ${year}`}</p>
            <p className="summary-value">
              {t(`${'invoiceValue'}`)}:{' '}
              <strong>{numbersHelper.convertMoneyValue(transactionObject?.transaction?.grossValue, i18n.language)}</strong>
            </p>
          </div>
        </section>

        <hr />

        <section className="upload-nf">
          {nf?.urlStorage && !isReplaceInvoice ? (
            <CardNfData
              nfData={nf}
              onChange={(isReplaceInvoice: boolean) => setIsReplaceInvoice(isReplaceInvoice)}
              transactionState={transactionObject?.transaction?.transactionStatus || ''}
            />
          ) : (
            <UploadInvoice onChange={(newNf: NfData) => setFileNf(newNf)} onValidate={(isValid: boolean) => setIsFileNfValid(isValid)} />
          )}
        </section>

        <hr />

        <section className="upload-xml">
          <CardXmlData
            nfData={nf}
            onChange={(newXml: XMLData) => {
              if (newXml?.file?.[0]) {
                setDateFile(newXml?.file?.[0])
                setIsReplaceInvoice(true)
              }
            }}
            isReplaceInvoice={isReplaceInvoice}
            onValidate={(hasError: boolean) => {
              setIsDateFileValid(hasError)
            }}
            transactionState={transactionObject?.transaction?.transactionStatus || ''}
          />
        </section>
      </div>
    </Modal>
  )
}
